import { DoctoraliaAuthentication } from "@/types/DoctoraliaAuthentication";
import { DoctoraliaIntegration } from "@/types/DoctoraliaIntegration";
import { RootState } from "@/types/RootState";
import { ConnectorApi } from "@/utils/connector-api";
import {
  getCurrentLicenseId,
  getCurrentUnitId,
  getCurrentUserIdInTable,
  isCurrentUserAdmin,
  isValidJWT,
} from "@/utils/helpers";
import { createStore } from "vuex";

export default createStore<RootState>({
  state: {
    aspSession: null,
    aspConfig: [],
    appTk: null,
    sidebarVisible: true,
    spaLoaded: false,
    dpSpacesCollapsed: false,
    integration: null,
    integrationToken: null,
    fetchIntegrationPromise: null,
    doctoraliaAuthentication: null,
  },
  getters: {},
  mutations: {
    setSidebarVisibility(state, visible) {
      state.sidebarVisible = visible;
    },
    setSpaLoaded(state, loaded) {
      state.spaLoaded = loaded;
    },
    setDpSpacesCollapsed(state, isCollapsed) {
      state.dpSpacesCollapsed = isCollapsed;
    },
    setAspSession(state, aspSession) {
      state.aspSession = aspSession;
    },
    setAspConfig(state, aspConfig) {
      state.aspConfig = aspConfig;
    },
    setAppTk(state, tk, setStorage = false) {
      state.appTk = tk;
      if (setStorage) {
        sessionStorage.setItem("tk", tk);
      }
    },
    setFetchIntegrationPromise(state, promise) {
      state.fetchIntegrationPromise = promise;
    },
    clearFetchIntegrationPromise(state) {
      state.fetchIntegrationPromise = null;
    },
    setIntegration(state, data: DoctoraliaIntegration | null) {
      state.integration = data;
    },
    setIntegrationToken(state, token) {
      state.integrationToken = token;
    },
    setDoctoraliaAuthentication(state, data: DoctoraliaAuthentication) {
      state.doctoraliaAuthentication = data;
    },
    clearIntegrationToken(state) {
      state.integrationToken = null;
      Object.keys(sessionStorage).forEach((key) => {
        if (key.startsWith("doctoraliaIntegrationToken")) {
          sessionStorage.removeItem(key);
        }
      });
    },
  },
  actions: {
    openSidebar({ commit }) {
      commit("setSidebarVisibility", true);
    },
    closeSidebar({ commit }) {
      commit("setSidebarVisibility", false);
    },
    toggleSidebar({ commit, state }) {
      commit("setSidebarVisibility", !state.sidebarVisible);
    },
    spaLoaded({ commit }) {
      commit("setSpaLoaded", true);
    },
    async fetchAspSession({ commit }) {
      const aspSession = window.feegow?.getAspSession();
      if (aspSession) {
        commit("setAspSession", aspSession);
      } else {
        console.error("User ASP session not found");
      }
    },
    async fetchAspConfig({ commit }) {
      const aspConfig = window.feegow?.getAspConfig();
      if (aspConfig) {
        commit("setAspConfig", aspConfig);
      } else {
        console.error("ASP config not found");
      }
    },
    async fetchIntegration({ state, commit }) {
      // Check current user ids
      const currentLicenseId = getCurrentLicenseId();
      const currentProfessionalId = getCurrentUserIdInTable();
      const currentUnitId = getCurrentUnitId();

      if (!currentLicenseId || !currentProfessionalId) {
        console.warn("[FetchIntegration] User not logged in");
        return; // If the user is not logged in, return without doing anything.
      }

      // If the data is already loaded, returns directly
      if (
        state.integration &&
        state.integration.clinic?.licenseId === +currentLicenseId &&
        state.integration.doctor?.doctorsLicenses.professionalId ===
          +currentProfessionalId
      ) {
        return state.integration;
      }
      // If a request is already in progress, returns the existing Promise
      if (state.fetchIntegrationPromise) {
        return state.fetchIntegrationPromise;
      }

      // If the data already exists in localStorage, save and use the existing data
      const storageData =
        sessionStorage.getItem(`doctoraliaClinic-${currentLicenseId}`) ||
        sessionStorage.getItem("doctoraliaClinic");

      if (storageData) {
        try {
          const currentIntegration = JSON.parse(storageData);
          if (
            currentIntegration &&
            +currentIntegration.licenseId === currentLicenseId
          ) {
            const integratedDoctor =
              ConnectorApi.findDoctorInFacilityIntegration(
                currentIntegration,
                currentProfessionalId
              );

            const integratedAddress =
              ConnectorApi.findAddressInDoctorIntegration(
                integratedDoctor,
                currentLicenseId,
                currentUnitId
              );

            if (integratedDoctor && integratedAddress) {
              console.log(
                "[Feegow SPA] [FetchIntegration] Using stored data for clinic"
              );
              const integration = {
                ...(state.integration || {}),
                ...{
                  clinic: currentIntegration,
                  doctor: integratedDoctor,
                  address: integratedAddress,
                },
              };
              commit("setIntegration", integration);
              return integration;
            }
          }
        } catch (e) {
          console.error(
            "[Feegow SPA] [FetchIntegration] Error while parsing doctoraliaClinic from local or session storage"
          );
        }
      }

      // Start a new request
      commit("clearIntegrationToken");

      const promise = ConnectorApi.fetchFacilityIntegration(currentLicenseId)
        .then(async (currentIntegration) => {
          sessionStorage.setItem(
            `doctoraliaClinic-${currentLicenseId}`,
            JSON.stringify(currentIntegration)
          );

          const integratedDoctor = ConnectorApi.findDoctorInFacilityIntegration(
            currentIntegration,
            currentProfessionalId
          );

          const integratedAddress = ConnectorApi.findAddressInDoctorIntegration(
            integratedDoctor,
            currentLicenseId,
            currentUnitId
          );

          const integration = {
            ...(state.integration || {}),
            ...{
              clinic: currentIntegration,
              ...(integratedDoctor ? { doctor: integratedDoctor } : {}),
              ...(integratedAddress ? { address: integratedAddress } : {}),
            },
          };

          commit("setIntegration", integration);

          sessionStorage.setItem(
            "doctoraliaClinic",
            JSON.stringify(currentIntegration)
          );

          return integration;
        })
        .catch((error) => {
          console.error(
            "[FetchIntegration] Error fetching Doctoralia integration data",
            error
          );
        })
        .finally(() => {
          commit("clearFetchIntegrationPromise");
        });
      commit("setFetchIntegrationPromise", promise);
      return promise;
    },
    async fetchDoctoraliaAuthentication({ commit, state }) {
      // If the data is already loaded in state, returns directly
      if (state.doctoraliaAuthentication) {
        return state.doctoraliaAuthentication;
      }

      const currentAuthenticationDataValue = sessionStorage.getItem(
        "doctoraliaAuthenticationData"
      );

      if (!currentAuthenticationDataValue) {
        console.debug(
          "[FetchDoctoraliaAuthentication] User not authenticated or not integrated with Doctoralia."
        );
        return;
      }

      try {
        const currentAuthenticationData = JSON.parse(
          currentAuthenticationDataValue
        );
        commit("setDoctoraliaAuthentication", currentAuthenticationData);
        return currentAuthenticationData;
      } catch (e) {
        console.error(
          "[FetchDoctoraliaAuthentication] Error parsing authentication data"
        );
        throw e;
      }
    },
    async fetchIntegrationToken({ state, commit, dispatch }) {
      const integrationData: DoctoraliaIntegration = await dispatch(
        "fetchIntegration"
      );

      const integratedDoctor = integrationData?.doctor;
      const integratedFacility = integrationData?.address?.facility;
      const integratedLicense =
        integrationData.clinic &&
        integrationData.clinic?.licenseId &&
        integrationData.clinic.apiClientId
          ? true
          : false;

      if (!integratedLicense && (!integratedDoctor || !integratedFacility)) {
        console.warn("[Feegow SPA] Doctor or address not integrated");
        throw new Error("Doctor or address not integrated");
      }

      if (!integrationData.clinic) {
        console.warn("[Feegow SPA] Missing clinic data.");
        throw new Error("Missing clinic data.");
      }

      if (!isCurrentUserAdmin() && !integratedDoctor) {
        // This conditional prevents an autologin token being fetch
        // if no integrated doctors are found (while the user is not an admin)
        console.warn("[Feegow SPA] Doctor not integrated.");
        throw new Error("Doctor not integrated.");
      }

      // Get a reference doctor integration to proceed with the autologin token
      // A reference is required if the current doctor is not integrated
      //   but we want to Admin authenticate, which then we need an API Client ID
      // An API Client ID will be provided through Connector by checking for a Facility association
      const refDoctorIntegration = integrationData.clinic.internalDoctors.find(
        (iDoctor) => {
          const activeDoctorAddress = iDoctor.addresses.find(
            (address) =>
              address.setupStatus === "success" &&
              address.licenseId === getCurrentLicenseId() &&
              address.feegowUnitId === getCurrentUnitId()
          );

          return iDoctor.setupStatus === "success" && activeDoctorAddress;
        }
      );

      const refDoctorAddress = refDoctorIntegration?.addresses.find(
        (address) =>
          address.setupStatus === "success" &&
          address.licenseId === getCurrentLicenseId() &&
          address.feegowUnitId === getCurrentUnitId()
      );

      if (!refDoctorIntegration || !refDoctorAddress) {
        console.warn(
          "[Feegow SPA] Admin authentication failed to find a reference doctor. Clinic needs at least one integrated active doctor."
        );
        throw new Error("No reference doctor found.");
      }

      if (
        state.integrationToken?.token &&
        state.integrationToken?.expires > Date.now()
      ) {
        return state.integrationToken;
      }

      const currentTokenKey = integratedDoctor
        ? `doctoraliaIntegrationToken-${integratedDoctor.doctoraliaId}`
        : `doctoraliaIntegrationClinicToken-${integrationData.clinic?.doctoraliaId}`;

      const storedToken = sessionStorage.getItem(currentTokenKey);

      if (storedToken) {
        const storedTokenData = JSON.parse(storedToken);

        if (storedTokenData.token && storedTokenData.expires > Date.now()) {
          commit("setIntegrationToken", storedTokenData);
          return storedTokenData;
        }
      }

      try {
        console.log("[Feegow SPA] Fetching autologin token");
        const preferredStage = integratedFacility
          ? ConnectorApi.ENVIRONMENT_STAGES[integratedFacility.environment]
          : refDoctorAddress.facility.environment;

        const newToken = await ConnectorApi.fetchAutoLoginToken(
          isCurrentUserAdmin(),
          integratedFacility
            ? integratedFacility.doctoraliaId
            : refDoctorAddress.facility.doctoraliaId,
          integratedDoctor
            ? integratedDoctor.doctoraliaId
            : refDoctorIntegration.doctoraliaId,
          preferredStage
        );

        const integrationToken = {
          // expires in 5 minutes
          token: newToken,
          expires: Date.now() + 300000,
        };

        commit("setIntegrationToken", integrationToken);

        sessionStorage.setItem(
          currentTokenKey,
          JSON.stringify(integrationToken)
        );
        return integrationToken;
      } catch (e) {
        commit("clearIntegrationToken");
        throw new Error("Failed to fetch autologin token");
      }
    },
    async fetchAppTk({ commit }) {
      const currentTk =
        sessionStorage.getItem("tk") ?? localStorage.getItem("tk");
      if (currentTk && isValidJWT(currentTk)) {
        commit("setAppTk", currentTk);
      }

      return currentTk;
    },
  },
  modules: {},
  plugins: [
    (store) => {
      // Called when the store is initialized

      store.dispatch("fetchAppTk").catch(console.error);
      store.dispatch("fetchDoctoraliaAuthentication").catch(console.error);

      // Listen for changes in storage (tk and doctoraliaAuthenticationData)
      window.addEventListener("storage", (event) => {
        if (event.key === "tk" && store.state.appTk !== event.newValue) {
          store.dispatch("fetchAppTk").catch(console.error);
        }
        if (
          event.key === "doctoraliaAuthenticationData" &&
          JSON.stringify(store.state.doctoraliaAuthentication) !==
            event.newValue
        ) {
          store.dispatch("fetchDoctoraliaAuthentication").catch(console.error);
        }
      });
    },
  ],
});
